import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentHYCSFundSuccessData,
  type EnrollmentHYCSFundSuccessSubmitInput,
  type EnrollmentHYCSFundSuccessSubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment HYCS FundSuccess data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentHYCSFundSuccessData>
 * @example const response = await getEnrollmentHYCSFundSuccessData()
 */
export const getEnrollmentHYCSFundSuccessData = async (
  context?: DefaultContext,
): Promise<EnrollmentHYCSFundSuccessData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentHYCSFundSuccessData',
      query: `mutation getEnrollmentHYCSFundSuccessData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentHYCSFundSuccessData }
  return response.submitEnrollment
}

export const useGetEnrollmentHYCSFundSuccessData = createQueryHook<undefined, EnrollmentHYCSFundSuccessData>(
  getEnrollmentHYCSFundSuccessData,
)

/**
 * Submit Enrollment HYCS FundSuccess data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentHYCSFundSuccessSubmitOutput>
 * @example const response = await submitEnrollmentHYCSFundSuccessData();
 */
export const submitEnrollmentHYCSFundSuccessData = async (
  enrollmentFundSuccessSubmitInput: EnrollmentHYCSFundSuccessSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentHYCSFundSuccessSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentHYCSFundSuccessData',
      query: `mutation submitEnrollmentHYCSFundSuccessData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentFundSuccessSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentHYCSFundSuccessSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentHYCSFundSuccessData = createMutationHook<
  EnrollmentHYCSFundSuccessSubmitInput,
  EnrollmentHYCSFundSuccessSubmitOutput
>(submitEnrollmentHYCSFundSuccessData)
